import { useFetcher } from '@remix-run/react';
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { Eraser, Loader } from 'lucide-react';
import { nanoid } from 'nanoid';
import { useEffect, useState } from 'react';
import { t } from '~/services/i18n';
import { useApplicationContext } from '../ApplicationContext';

import { Button } from '../ui/button';
import ChatBottombar from './chat-bottombar';
import ChatList from './chat-list';

interface ChatMessage {
    message: string;
    isMine: boolean;
    isEmpty?: boolean;
    error?: any;
}

const messageHistory = atomWithStorage<ChatMessage[]>('pw.message_history', [], undefined, {
    getOnInit: true,
});

const userChatId = atomWithStorage('pw.chat_id', '', undefined, {
    getOnInit: true,
});

export const chatDraftMessage = atomWithStorage(
    'pw.chat_draft',
    '',
    undefined,
    {
        getOnInit: true,
    },
);

export function ChatWidget({
    onBeforeSubmit,
    onAfterSubmit,
    onShowSignIn,
}: {
    onBeforeSubmit: () => boolean;
    onAfterSubmit: () => void;
    onShowSignIn?: () => void;
}) {
    const [items, setItems] = useAtom(messageHistory);
    const [chatId, setChatId] = useAtom(userChatId);
    const [documentContext, setDocumentContext] = useState<string>('');
    const [documentProperties, setDocumentProperties] = useState<
        Array<{ key: string; value: string }>
    >([]);
    const [isFetchingContext, setIsFetchingContext] = useState(false);
    const [contextProgress, setContextProgress] = useState<string>('');

    const applicationContext = useApplicationContext();
    const { isWord, isPowerPoint, isExcel, account } = applicationContext || {};
    const isAuthenticated = !!account;

    const clearHistory = () => {
        setItems([]);
        setChatId(nanoid());
    };
    const fetcher = useFetcher();
    const isFetching = fetcher.state !== 'idle';

    // Function to fetch Excel workbook context
    const fetchExcelContext = async () => {
        if (!isExcel) return;

        setIsFetchingContext(true);

        try {
            const Excel = (window as any).Excel;
            if (!Excel) {
                setIsFetchingContext(false);
                return;
            }

            await Excel.run(async (context: any) => {
                console.log(`getting excel data`);

                try {
                    const worksheets = context.workbook.worksheets;
                    context.load(worksheets, 'items');
                    await context.sync();

                    const sheetsContent = [];
                    const totalSheets = worksheets.items.length;

                    // Limits to prevent exceeding AI request limits
                    const MAX_SHEETS = 10;
                    const MAX_ROWS = 30;
                    const MAX_COLS = 30;

                    const sheetsToProcess = Math.min(totalSheets, MAX_SHEETS);

                    for (let i = 0; i < sheetsToProcess; i++) {
                        // Update progress
                        setContextProgress(
                            `Sheet ${i + 1}/${sheetsToProcess}${totalSheets > MAX_SHEETS ? ` (of ${totalSheets} total)` : ''}`,
                        );

                        const worksheet = worksheets.items[i];
                        context.load(worksheet, ['name']);
                        await context.sync();

                        try {
                            // Get used range (area with data)
                            const range = worksheet.getUsedRange();
                            context.load(range, [
                                'values',
                                'rowCount',
                                'columnCount',
                            ]);
                            await context.sync();

                            if (range && range.values) {
                                // Apply limits
                                const rowsToProcess = Math.min(
                                    range.rowCount,
                                    MAX_ROWS,
                                );
                                const colsToProcess = Math.min(
                                    range.columnCount,
                                    MAX_COLS,
                                );
                                const hasMoreData =
                                    range.rowCount > MAX_ROWS ||
                                    range.columnCount > MAX_COLS;

                                // Convert to CSV format
                                const csvLines = [];
                                for (let row = 0; row < rowsToProcess; row++) {
                                    const rowData = [];
                                    for (
                                        let col = 0;
                                        col < colsToProcess;
                                        col++
                                    ) {
                                        const cellValue =
                                            range.values[row][col];
                                        // Handle values that might contain commas or quotes
                                        if (
                                            cellValue !== null &&
                                            cellValue !== undefined
                                        ) {
                                            const strValue =
                                                cellValue.toString();
                                            if (
                                                strValue.includes(',') ||
                                                strValue.includes('"') ||
                                                strValue.includes('\n')
                                            ) {
                                                rowData.push(
                                                    `"${strValue.replace(/"/g, '""')}"`,
                                                );
                                            } else {
                                                rowData.push(strValue);
                                            }
                                        } else {
                                            rowData.push('');
                                        }
                                    }
                                    csvLines.push(rowData.join(','));
                                }

                                const sheetCsv = csvLines.join('\n');
                                if (sheetCsv.trim()) {
                                    const truncationNote = hasMoreData
                                        ? `\n[Note: Showing first ${rowsToProcess} rows and ${colsToProcess} columns. Full sheet has ${range.rowCount} rows and ${range.columnCount} columns]`
                                        : '';
                                    sheetsContent.push(
                                        `Sheet: ${worksheet.name}${truncationNote}\n${sheetCsv}`,
                                    );
                                }
                            }
                        } catch (sheetError) {
                            console.warn(
                                `Could not process sheet ${worksheet.name}:`,
                                sheetError,
                            );
                        }
                    }

                    // Add note if there are more sheets
                    if (totalSheets > MAX_SHEETS) {
                        sheetsContent.push(
                            `\n[Note: Showing first ${MAX_SHEETS} sheets of ${totalSheets} total sheets]`,
                        );
                    }

                    const workbookContent = sheetsContent.join('\n\n');
                    setDocumentContext(workbookContent);

                    // Set basic properties
                    const properties = [
                        { key: 'Sheets count', value: totalSheets.toString() },
                    ].filter((prop) => prop.value && prop.value.trim() !== '');

                    console.log({ properties, workbookContent });
                    setDocumentProperties(properties);
                } catch (innerError) {
                    console.warn(
                        'Could not get full workbook context:',
                        innerError,
                    );
                    // Set minimal context
                    setDocumentContext('Excel workbook');
                    setDocumentProperties([]);
                }
            });
        } catch (error) {
            console.error('Failed to fetch Excel context:', error);
        } finally {
            setIsFetchingContext(false);
            setContextProgress('');
        }
    };

    // Function to fetch PowerPoint presentation context
    const fetchPowerPointContext = async () => {
        if (!isPowerPoint) return;

        setIsFetchingContext(true);

        try {
            const PowerPoint = (window as any).PowerPoint;
            if (!PowerPoint) {
                setIsFetchingContext(false);
                return;
            }

            await PowerPoint.run(async (context: any) => {
                console.log(`getting powerpoint data`);

                try {
                    const slides = context.presentation.slides;
                    context.load(slides, 'items');
                    await context.sync();

                    // Get slides content with progress tracking
                    const slidesContent = [];
                    const totalSlides = slides.items.length;

                    // Limit to prevent exceeding AI request limits
                    const MAX_SLIDES = 20;
                    const slidesToProcess = Math.min(totalSlides, MAX_SLIDES);

                    for (let i = 0; i < slidesToProcess; i++) {
                        // Update progress
                        setContextProgress(
                            `Slide ${i + 1}/${slidesToProcess}${totalSlides > MAX_SLIDES ? ` (of ${totalSlides} total)` : ''}`,
                        );

                        const slide = slides.items[i];
                        try {
                            const shapes = slide.shapes;
                            context.load(shapes, 'items');
                            await context.sync();

                            const slideText = [];
                            for (let j = 0; j < shapes.items.length; j++) {
                                const shape = shapes.items[j];
                                try {
                                    // Try to get text from any shape that might have text
                                    context.load(shape, ['type', 'textFrame']);
                                    await context.sync();

                                    if (shape.textFrame) {
                                        context.load(
                                            shape.textFrame,
                                            'hasText',
                                        );
                                        await context.sync();

                                        if (shape.textFrame.hasText) {
                                            context.load(
                                                shape.textFrame.textRange,
                                                'text',
                                            );
                                            await context.sync();
                                            slideText.push(
                                                shape.textFrame.textRange.text,
                                            );
                                        }
                                    }
                                } catch (shapeError) {
                                    // Skip shapes that can't be processed
                                    console.warn(
                                        `Could not process shape ${j} on slide ${i + 1}:`,
                                        shapeError,
                                    );
                                }
                            }

                            if (slideText.length > 0) {
                                slidesContent.push(
                                    `Slide ${i + 1}: ${slideText.join(' ')}`,
                                );
                            }
                        } catch (slideError) {
                            console.warn(
                                `Could not process slide ${i + 1}:`,
                                slideError,
                            );
                        }
                    }

                    // Add note if there are more slides
                    if (totalSlides > MAX_SLIDES) {
                        slidesContent.push(
                            `[Note: Showing first ${MAX_SLIDES} slides of ${totalSlides} total slides]`,
                        );
                    }

                    const presentationText = slidesContent.join('\n\n');
                    setDocumentContext(presentationText);

                    // For properties, use a simpler approach since presentation metadata might not be available
                    const properties = [
                        { key: 'Slides count', value: totalSlides.toString() },
                    ].filter((prop) => prop.value && prop.value.trim() !== '');

                    console.log({ properties, presentationText });
                    setDocumentProperties(properties);
                } catch (innerError) {
                    console.warn(
                        'Could not get full presentation context:',
                        innerError,
                    );
                    // Set minimal context
                    setDocumentContext('PowerPoint presentation');
                    setDocumentProperties([]);
                }
            });
        } catch (error) {
            console.error('Failed to fetch PowerPoint context:', error);
        } finally {
            setIsFetchingContext(false);
            setContextProgress('');
        }
    };

    // Function to fetch Word document context
    const fetchWordContext = async () => {
        if (!isWord) return;

        setIsFetchingContext(true);

        try {
            const Word = (window as any).Word;
            if (!Word) {
                setIsFetchingContext(false);
                return;
            }

            await Word.run(async (context: any) => {
                console.log(`getting word data`);
                const documentBody = context.document.body;
                const documentProperties = context.document.properties;

                context.load(documentBody);
                context.load(documentProperties, [
                    'title',
                    'author',
                    'creationDate',
                    'lastAuthor',
                    'subject',
                    'template',
                    'category',
                    'applicationName',
                ]);

                await context.sync();

                const documentText = documentBody.text;
                setDocumentContext(documentText);

                const properties = [
                    { key: 'Document title', value: documentProperties.title },
                    {
                        key: 'Author',
                        value:
                            documentProperties.author ||
                            documentProperties.lastAuthor,
                    },
                    { key: 'Subject', value: documentProperties.subject },
                    { key: 'Category', value: documentProperties.category },
                    { key: 'Template', value: documentProperties.template },
                    {
                        key: 'Application',
                        value: documentProperties.applicationName,
                    },
                    {
                        key: 'Created',
                        value: documentProperties.creationDate
                            ? new Date(
                                  documentProperties.creationDate,
                              ).toLocaleDateString()
                            : '',
                    },
                ].filter((prop) => prop.value && prop.value.trim() !== '');

                setDocumentProperties(properties);
            });
        } catch (error) {
            console.error('Failed to fetch Word context:', error);
        } finally {
            setIsFetchingContext(false);
        }
    };

    useEffect(() => {
        if (!chatId) {
            setChatId(nanoid());
        }
    }, [chatId, setChatId]);

    // Fetch context when chat renders in Word, PowerPoint, or Excel
    useEffect(() => {
        if (isWord) {
            fetchWordContext();
        } else if (isPowerPoint) {
            fetchPowerPointContext();
        } else if (isExcel) {
            fetchExcelContext();
        }
    }, [isWord, isPowerPoint, isExcel]);

    useEffect(() => {
        if (!fetcher.data || fetcher.state !== 'idle') {
            return;
        }

        const payload = fetcher.data as any;

        // Check if this is a token limit error for unauthenticated users
        if (!isAuthenticated && payload.response && payload.response.includes('daily chat limit')) {
            // Show sign-in modal when free chat limit is reached
            if (onShowSignIn) {
                onShowSignIn();
            }
        }

        setItems((prevAssets) => [
            ...prevAssets,
            {
                isEmpty: payload.isEmptyResult,
                error: payload.error,
                message: payload.response,
                isMine: false,
            },
        ]);
    }, [fetcher.data, fetcher.state, isAuthenticated, onShowSignIn]);

    const addMessage = async (msg: string) => {
        // For authenticated users, use the original validation
        // For unauthenticated users, skip onBeforeSubmit to allow free bot usage
        if (isAuthenticated && !onBeforeSubmit()) {
            return false;
        }

        const MAX_HISTORY_LENGTH = 200;

        // Get fresh selected text before sending
        let currentSelectedText = '';
        if (isWord) {
            try {
                const Office = (window as any).Office;
                if (Office && Office.context && Office.context.document) {
                    await new Promise((resolve) => {
                        Office.context.document.getSelectedDataAsync(
                            Office.CoercionType.Text,
                            { valueFormat: 'unformatted' },
                            function (asyncResult: any) {
                                if (
                                    asyncResult.status ===
                                    Office.AsyncResultStatus.Succeeded
                                ) {
                                    currentSelectedText =
                                        asyncResult.value || '';
                                }
                                resolve(true);
                            },
                        );
                    });
                }
            } catch (error) {
                console.error('Failed to get selected text from Word:', error);
            }
        } else if (isExcel) {
            try {
                const Excel = (window as any).Excel;
                if (Excel) {
                    await Excel.run(async (context: any) => {
                        try {
                            const selectedRange =
                                context.workbook.getSelectedRange();
                            context.load(selectedRange, [
                                'values',
                                'rowCount',
                                'columnCount',
                            ]);
                            await context.sync();

                            if (selectedRange && selectedRange.values) {
                                // Apply same limits as context fetching
                                const MAX_ROWS = 30;
                                const MAX_COLS = 30;
                                const rowsToProcess = Math.min(
                                    selectedRange.rowCount,
                                    MAX_ROWS,
                                );
                                const colsToProcess = Math.min(
                                    selectedRange.columnCount,
                                    MAX_COLS,
                                );

                                // Convert selected range to CSV format
                                const csvLines = [];
                                for (let row = 0; row < rowsToProcess; row++) {
                                    const rowData = [];
                                    for (
                                        let col = 0;
                                        col < colsToProcess;
                                        col++
                                    ) {
                                        const cellValue =
                                            selectedRange.values[row][col];
                                        if (
                                            cellValue !== null &&
                                            cellValue !== undefined
                                        ) {
                                            const strValue =
                                                cellValue.toString();
                                            if (
                                                strValue.includes(',') ||
                                                strValue.includes('"') ||
                                                strValue.includes('\n')
                                            ) {
                                                rowData.push(
                                                    `"${strValue.replace(/"/g, '""')}"`,
                                                );
                                            } else {
                                                rowData.push(strValue);
                                            }
                                        } else {
                                            rowData.push('');
                                        }
                                    }
                                    csvLines.push(rowData.join(','));
                                }
                                currentSelectedText = csvLines.join('\n');

                                // Add note if selection was truncated
                                if (
                                    selectedRange.rowCount > MAX_ROWS ||
                                    selectedRange.columnCount > MAX_COLS
                                ) {
                                    currentSelectedText += `\n[Note: Showing first ${rowsToProcess} rows and ${colsToProcess} columns of ${selectedRange.rowCount}x${selectedRange.columnCount} selection]`;
                                }
                            }
                        } catch (selectionError) {
                            console.warn(
                                'Could not get Excel selection:',
                                selectionError,
                            );
                            // Excel selection might not be available, continue without it
                        }
                    });
                }
            } catch (error) {
                console.error('Failed to get selected data from Excel:', error);
            }
        } else if (isPowerPoint) {
            try {
                const PowerPoint = (window as any).PowerPoint;
                if (PowerPoint) {
                    await PowerPoint.run(async (context: any) => {
                        try {
                            const selectedShapes =
                                context.presentation.getSelectedShapes();
                            context.load(selectedShapes, 'items');
                            await context.sync();

                            const selectedTexts = [];
                            for (
                                let i = 0;
                                i < selectedShapes.items.length;
                                i++
                            ) {
                                const shape = selectedShapes.items[i];
                                try {
                                    context.load(shape, 'textFrame');
                                    await context.sync();

                                    if (shape.textFrame) {
                                        context.load(
                                            shape.textFrame,
                                            'hasText',
                                        );
                                        await context.sync();

                                        if (shape.textFrame.hasText) {
                                            context.load(
                                                shape.textFrame.textRange,
                                                'text',
                                            );
                                            await context.sync();
                                            selectedTexts.push(
                                                shape.textFrame.textRange.text,
                                            );
                                        }
                                    }
                                } catch (shapeError) {
                                    console.warn(
                                        'Could not get text from selected shape:',
                                        shapeError,
                                    );
                                }
                            }

                            currentSelectedText = selectedTexts.join(' ');
                        } catch (selectionError) {
                            console.warn(
                                'Could not get PowerPoint selection:',
                                selectionError,
                            );
                            // PowerPoint selection might not be available, continue without it
                        }
                    });
                }
            } catch (error) {
                console.error(
                    'Failed to get selected text from PowerPoint:',
                    error,
                );
            }
        }

        // Prepare the message with context if available
        let messageToSend = msg;
        if ((isWord || isPowerPoint || isExcel) && documentContext) {
            let appName = isWord
                ? 'Word'
                : isPowerPoint
                  ? 'PowerPoint'
                  : 'Excel';
            let contextInfo = `Context: User opened app in Microsoft ${appName}`;

            // Add document properties
            if (documentProperties.length > 0) {
                const docPropsText = documentProperties
                    .map((prop) => `${prop.key}: "${prop.value}"`)
                    .join(', ');
                contextInfo += `\n${docPropsText}`;
            }

            let contentLabel = isWord
                ? 'Document'
                : isPowerPoint
                  ? 'Presentation'
                  : 'Workbook';
            contextInfo += `\n\n${contentLabel} content: ${documentContext}`;

            if (currentSelectedText.trim()) {
                let selectionLabel = isExcel
                    ? 'Selected data (CSV format)'
                    : 'Selected text';
                contextInfo += `\n\n${selectionLabel}: ${currentSelectedText}`;
            }

            messageToSend = `User request: ${msg}\n\n${contextInfo}`;
        }

        // Use different endpoints based on authentication status
        const endpoint = isAuthenticated ? '/ai/bot' : '/ai/bot/free';
        
        fetcher.submit(
            { q: messageToSend, chatId },
            { method: 'POST', action: endpoint },
        );

        setItems([
            ...items.slice(-1 * MAX_HISTORY_LENGTH),
            { message: msg, isMine: true },
        ]);

        onAfterSubmit();
        return true;
    };

    return (
        <div className='flex flex-col w-full h-full relative'>
            {/* Loading overlay for context fetching */}
            {isFetchingContext && (
                <div className='absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-10'>
                    <div className='flex flex-col items-center space-y-2'>
                        <Loader className='w-6 h-6 animate-spin text-blue-600' />
                        <span className='text-sm text-gray-600'>
                            {contextProgress
                                ? `${t('chat.fetchingContext', 'Fetching document context...')} ${contextProgress}`
                                : t(
                                      'chat.fetchingContext',
                                      'Fetching document context...',
                                  )}
                        </span>
                    </div>
                </div>
            )}

            <div className='w-full flex flex-col flex-grow overflow-y-auto px-4 bg-slate-50'>
                <ChatList
                    messages={items}
                    isFetching={isFetching}
                    isWord={isWord}
                    isPowerPoint={isPowerPoint}
                    isExcel={isExcel}
                />
            </div>
            <div>
                <ChatBottombar
                    sendMessage={addMessage}
                    waitForResponse={isFetching || isFetchingContext}
                />
                <div className='flex flex-row justify-between items-center gap-x-2 px-1 pb-1 text-muted-foreground'>
                    <Button
                        variant={'ghost'}
                        className='text-xs'
                        onClick={clearHistory}>
                        <Eraser className='w-4 h-4 mr-1' />
                        {t('chat.clearHistory', 'Clear history')}
                    </Button>
                </div>
            </div>
        </div>
    );
}
